import {
  isEvaluatedWithoutPull,
  serializeLeaf,
  type StaleWarehouseConditionKind,
} from "src/components/audiences/utils";
import { type Condition, ConditionType } from "src/types/visual";
import type {
  JourneyExecutionMode,
  RealtimeEntryMethod,
} from "@hightouch/lib/journeys/types/config-types";

export type { RealtimeEntryMethod };

type CompiledLeaf = {
  type?: string;
  segment_id?: number | string;
  trait_definition_id?: string;
  event_model_id?: number | string;
  column_name?: string;
  relationship_id?: number | string;
};

const compiledLeaves = (conditions: unknown): CompiledLeaf[] => {
  if (conditions == null || typeof conditions !== "object") {
    return [];
  }
  const node = conditions as { type?: string; conditions?: unknown[] };
  if (node.type === "and" || node.type === "or") {
    return (node.conditions ?? []).flatMap(compiledLeaves);
  }
  return [node];
};

/**
 * The entry method a realtime entry node was authored with. Stored on the
 * config rather than re-derived, so the drawer, the graph validator and the
 * canvas cannot disagree about which of the three shapes a node carries.
 *
 * The server stamps it from the compiled condition tree on every save
 * (withRealtimeConditions), so a stored value can never drift from the tree the
 * loader reads. A batch entry-event node has no such field and reads as "event".
 */
export const realtimeEntryMethod = (config: unknown): RealtimeEntryMethod =>
  (config as { entry_method?: RealtimeEntryMethod } | null | undefined)
    ?.entry_method ?? "event";

/**
 * True when a realtime entry node enters on a condition rather than an event,
 * and so carries no event model in `segment_id`.
 */
export const isRealtimeConditionEntry = (config: unknown): boolean =>
  realtimeEntryMethod(config) !== "event";

export const authoredOrCompiledConditionTree = (config: unknown): unknown => {
  const nodeConfig = (config ?? {}) as {
    segment_conditions?: unknown;
    realtime_conditions?: unknown;
  };
  // The authoring field wins when present: it only exists while the drawer is
  // editing, so it carries the latest intent. A stale compiled leaf from the
  // previous entry method would otherwise pass an emptied condition.
  return nodeConfig.segment_conditions ?? nodeConfig.realtime_conditions;
};

export const realtimeConditionCount = (config: unknown): number =>
  compiledLeaves(authoredOrCompiledConditionTree(config)).length;

export const hasRealtimeCondition = (config: unknown): boolean =>
  realtimeConditionCount(config) > 0;

type AuthoringLeaf = CompiledLeaf & {
  modelId?: string | null;
  relationshipId?: number | string | null;
  property?:
    | string
    | {
        name?: string;
        column?: { name?: string; traitDefinitionId?: string } | null;
      }
    | null;
};

const propertyColumnName = (
  property: AuthoringLeaf["property"],
): string | undefined =>
  typeof property === "string"
    ? property
    : (property?.name ?? property?.column?.name);

/**
 * Maps a query-builder leaf onto the compiled shape so one reader serves both.
 * A compiled leaf passes through untouched.
 */
const asCompiledLeaf = (leaf: AuthoringLeaf): CompiledLeaf | undefined => {
  if (leaf.type === ConditionType.SegmentSet) {
    return leaf.modelId == null
      ? undefined
      : { type: "audience", segment_id: leaf.modelId };
  }
  if (leaf.type === ConditionType.Property) {
    const traitDefinitionId =
      typeof leaf.property === "object"
        ? leaf.property?.column?.traitDefinitionId
        : undefined;
    if (traitDefinitionId != null) {
      return { type: "trait", trait_definition_id: traitDefinitionId };
    }
    const columnName = propertyColumnName(leaf.property);
    return columnName == null
      ? undefined
      : { type: ConditionType.Property, column_name: columnName };
  }
  if (
    leaf.type === ConditionType.Event ||
    leaf.type === ConditionType.NumberOf
  ) {
    return leaf.relationshipId == null
      ? undefined
      : { type: leaf.type, relationship_id: leaf.relationshipId };
  }
  return leaf;
};

/**
 * The leaf that names a realtime entry node's trigger. An event entry compiles
 * its event leaf ahead of any profile criteria, so the first leaf is the trigger.
 */
export const realtimeEntryLeaf = (config: unknown): CompiledLeaf | undefined =>
  compiledLeaves(authoredOrCompiledConditionTree(config))
    .map((leaf) => asCompiledLeaf(leaf as AuthoringLeaf))
    .find((leaf) => leaf?.type != null);

const WAREHOUSE_LEAF_TYPES: ReadonlySet<string> = new Set([
  ConditionType.Property,
  ConditionType.Event,
  ConditionType.NumberOf,
]);

// Saved trees also hold compiled audience and event_performed leaves, which the audience helpers can't read.
export const warehouseLeaves = (
  trees: ReadonlyArray<unknown>,
  realtimeTraitIds: Set<string>,
): Condition[] =>
  (trees.flatMap(compiledLeaves) as Condition[]).filter(
    (leaf) =>
      WAREHOUSE_LEAF_TYPES.has(leaf.type) &&
      !isEvaluatedWithoutPull(leaf, realtimeTraitIds),
  );

export type WarehouseStaleCheck = {
  pulledContents: Set<string>;
  pulledIds: Set<string>;
  realtimeTraitIds: Set<string>;
};

export const buildWarehouseStaleCheck = (
  pulledTrees: ReadonlyArray<unknown>,
  realtimeTraitIds: Set<string>,
): WarehouseStaleCheck => {
  const pulledLeaves = warehouseLeaves(pulledTrees, realtimeTraitIds);
  return {
    pulledContents: new Set(pulledLeaves.map(serializeLeaf)),
    pulledIds: new Set(pulledLeaves.flatMap((leaf) => leaf.id ?? [])),
    realtimeTraitIds,
  };
};

// Duplicated tiles keep their condition ids, so whether the pull read a condition is decided by its content.
export const staleWarehouseConditions = (
  check: WarehouseStaleCheck,
  currentTrees: ReadonlyArray<unknown>,
): Map<string, StaleWarehouseConditionKind> => {
  const stale = new Map<string, StaleWarehouseConditionKind>();
  for (const leaf of warehouseLeaves(currentTrees, check.realtimeTraitIds)) {
    if (leaf.id && !check.pulledContents.has(serializeLeaf(leaf))) {
      stale.set(leaf.id, check.pulledIds.has(leaf.id) ? "modified" : "added");
    }
  }
  return stale;
};

// Keyed by the column the warehouse pull reads, a parent model column by name or a related model's raw column.
export const mappedWarehouseColumns = (
  subscriptionConfigs: ReadonlyArray<unknown>,
  parentModelPrimaryKey: string | null | undefined,
): Set<string> => {
  const columns = new Set<string>();
  for (const config of subscriptionConfigs) {
    const syncConfig = (config as { syncConfig?: unknown } | null)?.syncConfig;
    if (syncConfig == null || typeof syncConfig !== "object") {
      continue;
    }
    for (const value of Object.values(syncConfig)) {
      for (const mapping of Array.isArray(value) ? value : [value]) {
        const from = (mapping as { from?: unknown } | null)?.from;
        // The worker reads the primary key from the trigger, so mapping it needs no pull.
        if (from === parentModelPrimaryKey) {
          continue;
        }
        const column = warehouseColumnKey(from);
        if (column != null) {
          columns.add(column);
        }
      }
    }
  }
  return columns;
};

const warehouseColumnKey = (from: unknown): string | undefined => {
  if (typeof from === "string") {
    return from === "" ? undefined : JSON.stringify(from);
  }
  const related = from as {
    type?: unknown;
    path?: unknown;
    column?: { type?: unknown; modelId?: unknown; name?: unknown };
  } | null;
  if (
    related?.type !== "related" ||
    !Array.isArray(related.path) ||
    related.column?.type !== "raw"
  ) {
    return undefined;
  }
  return JSON.stringify([
    related.path.map(String),
    String(related.column.modelId),
    related.column.name,
  ]);
};

export const hasWarehouseCondition = (
  config: unknown,
  realtimeTraitIds: Set<string>,
): boolean =>
  warehouseLeaves([authoredOrCompiledConditionTree(config)], realtimeTraitIds)
    .length > 0;

/**
 * Narrows a stored execution_mode to the union. The column is nullable and
 * LLM-authored journeys have no row yet, so anything but "realtime" is batch.
 */
export const toExecutionMode = (mode: unknown): JourneyExecutionMode =>
  mode === "realtime" ? "realtime" : "batch";

// The compiled trees, since those are what the warehouse pull reads.
export const realtimeJourneyConditionTrees = (
  nodeConfigs: ReadonlyArray<unknown>,
  exitCriteria: unknown,
): Condition[] =>
  [...nodeConfigs, exitCriteria]
    .map(
      (config) =>
        (config as { realtime_conditions?: unknown } | null)
          ?.realtime_conditions,
    )
    .filter(
      (tree): tree is Condition => tree != null && typeof tree === "object",
    );
